import * as React from "react";
import {
  flexRender,
  useTable,
  tableFeatures,
  rowPaginationFeature,
  rowSortingFeature,
  rowSelectionFeature,
  columnVisibilityFeature,
  createPaginatedRowModel,
  createSortedRowModel,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Skeleton } from "./skeleton";
import {
  DataTablePagination,
  PAGE_SIZE_OPTIONS,
} from "./data-table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

/** Default hub list: matches Maintenance / inbox-style tables (20 rows per page). */
const DATA_TABLE_INITIAL_PAGINATION = Object.freeze({
  pageIndex: 0,
  pageSize: 20,
});

/** Secondary blocks under the main queue (e.g. invoices) — fewer rows per page. */
const DATA_TABLE_INITIAL_PAGINATION_SMALL = Object.freeze({
  pageIndex: 0,
  pageSize: 10,
});

const DEFAULT_DATA_TABLE_TRANSLATIONS = {
  "dataTable.rowsPerPage": "Rows per page",
  "dataTable.noResults": "No results found.",
  "dataTable.sortAscending": "Sort ascending",
  "dataTable.sortDescending": "Sort descending",
  "dataTable.clearSort": "Clear sort",
  "common:previous": "Previous",
  "common:next": "Next",
};

const DataTableDensityContext = React.createContext({
  density: "compact",
});

/**
 * Sortable column header (use inside column `header` for string titles).
 * `compact` follows parent `DataTable` `density` when omitted (default: compact hub style).
 *
 * @param {{ column: import('@tanstack/react-table').Column<unknown, unknown>, title: string, className?: string, compact?: boolean }} props
 */
function DataTableColumnHeader({ column, title, className, compact, translations = {} }) {
  const t = (k, fb) => translations[k] || fb || k;
  const { density } = React.useContext(DataTableDensityContext);
  const isCompact = compact ?? density === "compact";

  if (!column.getCanSort()) {
    return (
      <span
        className={cn(
          isCompact
            ? "text-overline tracking-wider"
            : "text-xs font-semibold uppercase tracking-wider text-muted-foreground",
          className,
        )}
      >
        {title}
      </span>
    );
  }
  const sorted = column.getIsSorted();
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        isCompact
          ? "-ml-2 h-8 min-h-8 px-2 text-overline tracking-wider hover:text-foreground hover:bg-transparent data-[state=open]:bg-transparent"
          : "-ml-3 h-11 min-h-[44px] px-3 font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-transparent data-[state=open]:bg-transparent",
        className,
      )}
      onClick={column.getToggleSortingHandler()}
      aria-label={
        sorted === "asc"
          ? t("sortDescending", "Sort descending")
          : sorted === "desc"
            ? t("clearSort", "Clear sort")
            : t("sortAscending", "Sort ascending")
      }
    >
      {title}
      {sorted === "desc" ? (
        <ArrowDown className="ml-1.5 h-3.5 w-3.5 shrink-0" />
      ) : sorted === "asc" ? (
        <ArrowUp className="ml-1.5 h-3.5 w-3.5 shrink-0" />
      ) : (
        <ArrowUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
      )}
    </Button>
  );
}

/**
 * @typedef {import('@tanstack/react-table').ColumnDef<TData, TValue>} DataTableColumnDef
 */

/**
 * Data table with client-side or server-side pagination and sorting.
 *
 * **Client mode (default):** sorts and paginates in-memory.
 *
 * **Server mode:** set `serverSide` and pass `pageCount` (and usually `rowCount`).
 * Use `onPaginationChange` / `onSortingChange` to refetch. When `serverSide` is true,
 * sorting and pagination are manual (handled by the parent + API).
 *
 * @template TData
 * @param {{
 *   columns: import('@tanstack/react-table').ColumnDef<TData, unknown>[],
 *   data: TData[],
 *   serverSide?: boolean,
 *   pageCount?: number,
 *   rowCount?: number,
 *   initialPagination?: import('@tanstack/react-table').PaginationState,
 *   initialSorting?: import('@tanstack/react-table').SortingState,
 *   onPaginationChange?: (updater: import('@tanstack/react-table').Updater<import('@tanstack/react-table').PaginationState>) => void,
 *   onSortingChange?: (updater: import('@tanstack/react-table').Updater<import('@tanstack/react-table').SortingState>) => void,
 *   className?: string,
 *   tableClassName?: string,
 *   emptyMessage?: string,
 *   onRowClick?: (row: TData) => void,
 *   density?: "default" | "compact",
 * }} props
 */
function DataTable({
  columns,
  data,
  serverSide = false,
  pageCount: serverPageCount,
  rowCount: serverRowCount,
  initialPagination = DATA_TABLE_INITIAL_PAGINATION,
  initialSorting = [],
  onPaginationChange,
  onSortingChange,
  className,
  tableClassName,
  emptyMessage,
  onRowClick,
  density = "compact",
  renderMobileCard,
  isLoading = false,
  translations = {},
}) {
  const t = (k, fb) => {
    if (typeof translations === "function") return translations(k, fb);
    return (
      (translations && translations[k]) ||
      fb ||
      DEFAULT_DATA_TABLE_TRANSLATIONS[k] ||
      k
    );
  };
  const [pagination, setPagination] = React.useState(initialPagination);
  const [sorting, setSorting] = React.useState(initialSorting);

  const handlePaginationChange = React.useCallback(
    (updater) => {
      setPagination((old) => {
        const next = typeof updater === "function" ? updater(old) : updater;
        onPaginationChange?.(next);
        return next;
      });
    },
    [onPaginationChange],
  );

  const handleSortingChange = React.useCallback(
    (updater) => {
      setSorting((old) => {
        const next = typeof updater === "function" ? updater(old) : updater;
        onSortingChange?.(next);
        return next;
      });
    },
    [onSortingChange],
  );

  const features = React.useMemo(() => tableFeatures({
    rowSelectionFeature,
    columnVisibilityFeature,
    ...(!serverSide && {
      rowPaginationFeature,
      rowSortingFeature,
      paginatedRowModel: createPaginatedRowModel(),
      sortedRowModel: createSortedRowModel(),
    }),
  }), [serverSide]);

  const table = useTable({
    features,
    data,
    columns,
    defaultColumn: {
      enableSorting: true,
    },
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    ...(serverSide
      ? {
          manualPagination: true,
          manualSorting: true,
          pageCount: serverPageCount ?? 1,
        }
      : {}),
  });

  const pageIndex = table.state.pagination.pageIndex;
  const pageSize = table.state.pagination.pageSize;
  const pageCount = table.getPageCount();
  const totalRows =
    serverSide && typeof serverRowCount === "number"
      ? serverRowCount
      : data.length;
  const startRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  const showPagination = !isLoading && totalRows > 0;

  const compact = density === "compact";

  const renderDesktopTable = () => (
    <div
      className={cn(
        "border border-border/85 bg-card surface-fade shadow-premium overflow-hidden",
        compact ? "rounded-xl" : "rounded-card",
      )}
    >
      <Table
        className={cn(tableClassName, compact && "table-density-compact")}
      >
        <TableHeader className="table-header-fade">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="hover:bg-transparent border-border/80"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={header.column.columnDef.meta?.headerClassName}
                  aria-sort={
                    header.column.getCanSort()
                      ? header.column.getIsSorted() === "desc"
                        ? "descending"
                        : header.column.getIsSorted() === "asc"
                          ? "ascending"
                          : "none"
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: Math.min(pageSize || 5, 5) }).map(
              (_, rowIndex) => (
                <TableRow
                  key={`skeleton-row-${rowIndex}`}
                  className="hover:bg-transparent"
                >
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={`skeleton-cell-${colIndex}`}
                      className={col.meta?.cellClassName}
                    >
                      <Skeleton className="h-4 w-full min-w-[3rem] max-w-[12rem]" />
                    </TableCell>
                  ))}
                </TableRow>
              ),
            )
          ) : table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={
                  onRowClick
                    ? (e) => {
                        const interactive = e.target.closest(
                          'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"]',
                        );
                        if (interactive && interactive !== e.currentTarget) return;
                        onRowClick(row.original);
                      }
                    : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        const interactive = e.target.closest(
                          'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"]',
                        );
                        if (interactive && interactive !== e.currentTarget) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
                className={cn(
                  onRowClick && "cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cell.column.columnDef.meta?.cellClassName}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className={cn(
                  "text-center text-muted-foreground",
                  compact ? "h-16 py-6" : "h-24",
                )}
              >
                {emptyMessage ?? t("dataTable.noResults")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <DataTableDensityContext.Provider value={{ density }}>
      <div className={cn("space-y-4 w-full min-w-0", className)}>
        {renderMobileCard ? (
          <>
            {/* Mobile Card List View (<md) */}
            <div className="space-y-3 md:hidden">
              {isLoading ? (
                Array.from({ length: Math.min(pageSize || 5, 5) }).map(
                  (_, i) => (
                    <div
                      key={`mobile-skeleton-${i}`}
                      className="w-full rounded-card border border-border bg-card p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <Skeleton className="h-5 w-2/5" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </div>
                  ),
                )
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row, index) => (
                  <div
                    key={row.id}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={
                      onRowClick
                        ? (e) => {
                            const interactive = e.target.closest(
                              'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"]',
                            );
                            if (interactive && interactive !== e.currentTarget) return;
                            onRowClick(row.original);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            const interactive = e.target.closest(
                              'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"]',
                            );
                            if (interactive && interactive !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onRowClick(row.original);
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      onRowClick &&
                        "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl",
                    )}
                  >
                    {renderMobileCard(row.original, index, row)}
                  </div>
                ))
              ) : (
                <div
                  className={cn(
                    "rounded-card border border-dashed border-border p-8 text-center text-sm text-muted-foreground",
                    compact && "rounded-lg py-6",
                  )}
                >
                  {emptyMessage ?? t("dataTable.noResults")}
                </div>
              )}
            </div>

            {/* Desktop Table View (>=md) */}
            <div className="hidden md:block">
              <div className="w-full overflow-x-auto">
                {renderDesktopTable()}
              </div>
            </div>
          </>
        ) : (
          /* Fallback when renderMobileCard is NOT provided */
          <div className="w-full overflow-x-auto">
            {renderDesktopTable()}
          </div>
        )}

        {showPagination && (
          <DataTablePagination
            table={table}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageCount={pageCount}
            startRow={startRow}
            endRow={endRow}
            totalRows={totalRows}
            t={t}
          />
        )}
      </div>
    </DataTableDensityContext.Provider>
  );
}

export {
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  DATA_TABLE_INITIAL_PAGINATION,
  DATA_TABLE_INITIAL_PAGINATION_SMALL,
  PAGE_SIZE_OPTIONS,
};

export default DataTable;

